import { t } from "i18next";
import { Schemas } from "../../types";
import { boolean, object, string, number, mixed, ObjectSchema } from "yup";

type GapVerificationOption = {
  key: "statement" | "documentation" | "reference";
  label: string;
  value: Schemas["GapVerificationTypeEnum"];
  description: string;
};

export type GapThresholdUnit = "MONTHS" | "DAYS";

/** OpenAPI-generated enum; keep in sync with experience template `verification_type`. */
export type EducationVerificationType = Schemas["ExperienceEducationLevelEnum"];

export type ExperienceVerificationTemplateFormType = {
  name?: string;
  lookback_period_years: number;
  /** When false, gap threshold and gap verification fields are not sent to the API. */
  include_gap_detection: boolean;
  /** Required when `include_gap_detection` is true; may be unset when gaps are off. */
  gap_threshold_unit?: GapThresholdUnit;
  minimum_gap_period_months?: number;
  minimum_gap_period_days?: number;
  /** Required when `include_gap_detection` is true. */
  gap_verification_type?: Schemas["GapVerificationTypeEnum"];
  reference_rule_options?: Schemas["ReferenceRuleOptionsEnum"];
  include_cumulative_gap_detection: boolean;
  verification_type?: EducationVerificationType;
  digital_verification?: boolean;
};

export const EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG = {
  // Lookback
  MINIMUM_LOOKBACK_PERIOD_MONTHS: 12,
  MAXIMUM_LOOKBACK_PERIOD_MONTHS: 120,
  DEFAULT_LOOKBACK_PERIOD_MONTHS: 36,
  // Gap — months
  MINIMUM_GAP_PERIOD_MONTHS: 1,
  MAXIMUM_GAP_PERIOD_MONTHS: 100,
  DEFAULT_MINIMUM_GAP_PERIOD_MONTHS: 6,
  // Gap — days
  MINIMUM_GAP_PERIOD_DAYS: 1,
  MAXIMUM_GAP_PERIOD_DAYS: 1000,
};

export const EDUCATION_LEVEL_OPTIONS = (): {
  label: string;
  value: EducationVerificationType;
}[] => [
  {
    label: t("Only education within lookback period"),
    value: "EDUCATION_VERIFICATIONS_WITHIN_LOOKBACK",
  },
  {
    label: t("All provided education"),
    value: "EDUCATION_VERIFICATIONS_ALL",
  },
  {
    label: t("All university/tertiary/post-secondary level"),
    value: "EDUCATION_VERIFICATIONS_ALL_POST_SECONDARY",
  },
  {
    label: t("Highest level of completed education"),
    value: "EDUCATION_VERIFICATIONS_HIGHEST_LEVEL",
  },
];

export const GAP_THRESHOLD_UNIT_OPTIONS = (): {
  label: string;
  value: GapThresholdUnit;
}[] => [
  { label: t("Months"), value: "MONTHS" },
  { label: t("Days"), value: "DAYS" },
];

export const GAP_PERIOD_MONTHS_OPTIONS = ({
  hideBPSSReference = false,
}: {
  hideBPSSReference?: boolean;
} = {}) => {
  const min = EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.MINIMUM_GAP_PERIOD_MONTHS;
  const max = EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.MAXIMUM_GAP_PERIOD_MONTHS;
  const candidates = [min, 2, 3, 4, 5, 6, 12, max];
  const values = Array.from(new Set(candidates))
    .filter((v) => v >= min && v <= max)
    .sort((a, b) => a - b);

  return values.map((value) => {
    let label: string;
    if (value === 1) {
      label = "1 month";
    } else if (value === max) {
      label = `${max} months`;
    } else if (value === 6) {
      label = hideBPSSReference ? "6 months" : "6 months (BPSS default)";
    } else {
      label = `${value} months`;
    }
    return { label, value };
  });
};

function gapAtLeastMonthsMessage(months: number): string {
  return months === 1
    ? t("Minimum gap length must be at least 1 month.")
    : t("Minimum gap length must be at least {{months}} months.", { months });
}

function gapAtMostMonthsMessage(months: number): string {
  return months === 1
    ? t("Minimum gap length must be at most 1 month.")
    : t("Minimum gap length must be at most {{months}} months.", { months });
}

export const REFERENCE_RULE_OPTIONS: {
  label: string;
  value: Schemas["ReferenceRuleOptionsEnum"];
}[] = [
  {
    label: "Default reference rules",
    value: "DEFAULT",
  },
  { label: "BPSS reference rules", value: "BPSS" },
];

export const validationSchema: ObjectSchema<ExperienceVerificationTemplateFormType> =
  object().shape({
    name: string().trim().min(1, "Name cannot be empty").required(),
    lookback_period_years: number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue == null ? undefined : value
      )
      .typeError(t("Enter a whole number."))
      .integer(t("Enter a whole number."))
      .required(t("Enter how many years to look back."))
      .min(
        EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.MINIMUM_LOOKBACK_PERIOD_MONTHS /
          12,
        t("Experience lookback period must be at least {{minValue}} years.", {
          minValue:
            EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.MINIMUM_LOOKBACK_PERIOD_MONTHS /
            12,
        })
      )
      .max(
        EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.MAXIMUM_LOOKBACK_PERIOD_MONTHS /
          12,
        t("Experience lookback period must be at most {{maxValue}} years.", {
          maxValue:
            EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.MAXIMUM_LOOKBACK_PERIOD_MONTHS /
            12,
        })
      ),
    include_gap_detection: boolean().default(true),
    gap_threshold_unit: mixed<GapThresholdUnit>()
      .oneOf(["MONTHS", "DAYS"])
      .when("include_gap_detection", {
        is: true,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional(),
      }),
    minimum_gap_period_months: number().when(
      ["include_gap_detection", "gap_threshold_unit"],
      {
        is: (includeGap: boolean, unit: GapThresholdUnit | undefined) =>
          includeGap === true && unit === "MONTHS",
        then: (schema) =>
          schema
            .transform((value, originalValue) =>
              originalValue === "" || originalValue == null ? undefined : value
            )
            .typeError(t("Enter a whole number."))
            .integer(t("Enter a whole number."))
            .required(t("Enter the minimum gap length."))
            .min(
              EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.MINIMUM_GAP_PERIOD_MONTHS,
              gapAtLeastMonthsMessage(
                EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.MINIMUM_GAP_PERIOD_MONTHS
              )
            )
            .max(
              EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.MAXIMUM_GAP_PERIOD_MONTHS,
              gapAtMostMonthsMessage(
                EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.MAXIMUM_GAP_PERIOD_MONTHS
              )
            ),
        otherwise: (schema) => schema.optional().nullable(),
      }
    ),
    minimum_gap_period_days: number().when(
      ["include_gap_detection", "gap_threshold_unit"],
      {
        is: (includeGap: boolean, unit: GapThresholdUnit | undefined) =>
          includeGap === true && unit === "DAYS",
        then: (schema) =>
          schema
            .transform((value, originalValue) =>
              originalValue === "" || originalValue == null ? undefined : value
            )
            .typeError(t("Enter a whole number."))
            .integer(t("Enter a whole number."))
            .required(t("Enter the minimum gap length."))
            .min(
              EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.MINIMUM_GAP_PERIOD_DAYS,
              t("Minimum gap length must be at least {{minValue}} days.", {
                minValue:
                  EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.MINIMUM_GAP_PERIOD_DAYS,
              })
            )
            .max(
              EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.MAXIMUM_GAP_PERIOD_DAYS,
              t("Minimum gap length must be at most {{maxValue}} days.", {
                maxValue:
                  EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.MAXIMUM_GAP_PERIOD_DAYS,
              })
            ),
        otherwise: (schema) => schema.optional().nullable(),
      }
    ),
    gap_verification_type: mixed<Schemas["GapVerificationTypeEnum"]>()
      .oneOf(["REFERENCE", "STATEMENT", "DOCUMENTATION"])
      .when("include_gap_detection", {
        is: true,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional(),
      }),
    reference_rule_options: mixed<Schemas["ReferenceRuleOptionsEnum"]>()
      .oneOf(["BPSS", "DEFAULT"])
      .when(["include_gap_detection", "gap_verification_type"], {
        is: (includeGap: boolean, gapType: string | undefined) =>
          includeGap === true && gapType === "REFERENCE",
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional(),
      }),
    include_cumulative_gap_detection: boolean().default(false),
    verification_type: mixed<EducationVerificationType>()
      .oneOf([
        "EDUCATION_VERIFICATIONS_WITHIN_LOOKBACK",
        "EDUCATION_VERIFICATIONS_ALL",
        "EDUCATION_VERIFICATIONS_ALL_POST_SECONDARY",
        "EDUCATION_VERIFICATIONS_HIGHEST_LEVEL",
      ])
      .optional(),
    digital_verification: boolean().optional(),
  });

export const GAP_VERIFICATION_OPTIONS = (): GapVerificationOption[] => [
  {
    key: "statement",
    label: t("Statement only"),
    value: "STATEMENT",
    description: t("The applicant provides a written explanation of the gap."),
  },
  {
    key: "documentation",
    label: t("Statement and documentation"),
    value: "DOCUMENTATION",
    description: t(
      "The applicant provides an explanation and documentation. Gap document verification will be performed and billed separately for each gap."
    ),
  },
  {
    key: "reference",
    label: t("Statement and reference"),
    value: "REFERENCE",
    description: t(
      "The applicant provides a written explanation and nominates a referee to confirm the gap period. A reference verification will be performed and billed separately for each gap."
    ),
  },
];
